import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';
import API_ENDPOINTS from '../config/api';
import { 
  User, 
  MapPin, 
  Calendar, 
  BookOpen, 
  Award, 
  Star, 
  Download,
  Edit,
  Mail,
  Link as LinkIcon,
  Github,
  Twitter,
  Linkedin,
  BarChart3,
  TrendingUp,
  Users,
  MessageSquare,
  Heart,
  Share2,
  Camera,
  ChevronRight
} from 'lucide-react';

const Profile = () => {
  const { id } = useParams();
  const { getToken, userId: clerkUserId } = useAuth();  
  const { user: clerkUser } = useUser();
  const [activeTab, setActiveTab] = useState('papers');
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const fileInputRef = useRef(null);
  // NEW: State for papers
  const [userPapers, setUserPapers] = useState([]); 
  const [papersLoading, setPapersLoading] = useState(false);
  const [papersError, setPapersError] = useState(null);
  // Get the actual user ID - handle 'me' as special case for current user
  // Use 'default-user' as fallback if no user ID is available
  let profileUserId;
  if (id === 'me' || !id) {
    profileUserId = clerkUserId || 'default-user';
  } else {
    profileUserId = id;
  }
  
  // Check if viewing own profile
  // If no ID in URL or 'me', it's the current user's profile
  // Or if the profile ID matches the current user ID
  const isOwnProfile = !id || id === 'me' || (profileUserId === clerkUserId) || (!clerkUserId && profileUserId === 'default-user');
    // Default profile structure with stats and badges - use Clerk user data if available
  const defaultProfileData = useMemo(() => ({
    id: profileUserId,
    name: clerkUser?.fullName || clerkUser?.firstName || clerkUser?.username || 'New User',
    title: '',
    affiliation: '',
    location: '',
    joinDate: clerkUser?.createdAt 
      ? new Date(clerkUser.createdAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
      : new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),    avatar: clerkUser?.imageUrl || null,
    bio: '',
    email: clerkUser?.primaryEmailAddress?.emailAddress || '',
    website: '',
    institutionalEmail: '',
    orcid: '',
    googleScholarUrl: '',
    openreviewId: '',
    academicStatus: '',
    defaultCategory: '',
    isVerified: false, // Verification status
    socialLinks: {
      github: '',
      twitter: '',
      linkedin: ''
    },
    stats: {
      papers: 47,
      citations: 2847,
      hIndex: 23,
      followers: 1204,
      following: 342,
      downloads: 15420
    },
    badges: []
    /*
      { name: 'Top Reviewer', icon: Award, color: 'bg-yellow-100 text-yellow-800' },
      { name: 'Rising Star', icon: Star, color: 'bg-purple-100 text-purple-800' },
      { name: 'Community Leader', icon: Users, color: 'bg-blue-100 text-blue-800' }
    */
  }), [profileUserId, clerkUser]);

  // Fetch profile data on component mount or when user ID changes
  const fetchProfileData = useCallback(async () => {
    try {
      const token = await getToken();
      const response = await fetch(API_ENDPOINTS.PROFILE_GET(profileUserId), {
        headers: {
          'Authorization': token ? `Bearer ${token}` : undefined
        }
      });      if (response.ok) {
        const data = await response.json();
        // Map backend data to frontend format, preferring saved data over Clerk defaults
        setProfileData({
          ...defaultProfileData,
          id: data.user_id,
          name: data.name || defaultProfileData.name,
          title: data.title || '',
          affiliation: data.affiliation || '',
          location: data.location || '',
          avatar: data.avatar_url || defaultProfileData.avatar,
          bio: data.bio || '',
          email: data.email || defaultProfileData.email,
          website: data.website || '',
          institutionalEmail: data.institutional_email || '',
          orcid: data.orcid || '',
          googleScholarUrl: data.google_scholar_url || '',
          openreviewId: data.openreview_id || '',
          academicStatus: data.academic_status || '',
          defaultCategory: data.default_category || '',
          isVerified: data.is_verified === 1 || data.is_verified === true, // Convert to boolean
          socialLinks: {
            github: data.github_url || '',
            twitter: data.twitter_url || '',
            linkedin: data.linkedin_url || ''
          }
        });
      } else if (response.status === 404) {
        // Profile not found, use default data with Clerk info
        setProfileData(defaultProfileData);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      setProfileData(defaultProfileData);
    }
  }, [getToken, profileUserId, defaultProfileData]);
 const fetchUserPapers = useCallback(async () => {
    if (!profileUserId) return;
    setPapersLoading(true);
    setPapersError(null);
    try {
        const token = await getToken();
        const submissionsUrl = `${API_ENDPOINTS.CLEAN_BASE_URL}/api/submissions?user_id=${profileUserId}&skip=0&limit=100`;
        console.log(`[DEBUG] Papers URL: ${submissionsUrl}`); 

        const response = await fetch(submissionsUrl, { 
            headers: {
                'Authorization': token ? `Bearer ${token}` : undefined
            }
        });

        if (!response.ok) {
            // IMPORTANT: If response is NOT OK (e.g., 500, 404),
            // we read it as text instead of JSON to log the actual error message.
            const errorText = await response.text(); 
            console.error('API responded with error/HTML:', errorText);
            console.error('API Error Response:', errorText);
            // If the server returns HTML (the 'Unexpected token <'), the problem is here.
            // Throw a custom error with better status context.
            throw new Error(`Failed to fetch user submissions. Status: ${response.status}. See console for details.`);
        }
        const data = await response.json();
        // Assuming the backend returns an array of paper objects directly
        setUserPapers(data || []);

    } catch (error) {
        console.error('Error fetching user papers:', error);
        setPapersError('Failed to load submissions.');
        setUserPapers([]);
    } finally {
        setPapersLoading(false);
    }
}, [getToken, profileUserId]); // Dependencies include auth token and current profile user ID
  
  useEffect(() => {
    if (profileUserId) {
      fetchProfileData();
      fetchUserPapers();
    }
  }, [profileUserId, fetchProfileData, fetchUserPapers]);

  // Show loading state while fetching profile
  if (!profileData) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-gray-500">Loading profile...</div>
      </div>
    );
  }
  // Initialize form data when entering edit mode
  const handleEditClick = () => {
    setIsEditMode(true);
    setFormData({
      name: profileData.name,
      title: profileData.title,
      affiliation: profileData.affiliation,
      location: profileData.location,
      bio: profileData.bio,
      email: profileData.email,
      website: profileData.website,
      institutionalEmail: profileData.institutionalEmail,
      orcid: profileData.orcid,
      googleScholarUrl: profileData.googleScholarUrl,
      openreviewId: profileData.openreviewId,
      academicStatus: profileData.academicStatus,
      defaultCategory: profileData.defaultCategory,
      github: profileData.socialLinks.github,
      twitter: profileData.socialLinks.twitter,
      linkedin: profileData.socialLinks.linkedin
    });
    setAvatarPreview(null);
    setAvatarFile(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }

      setAvatarFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  const handleAvatarUpload = async () => {
    if (!avatarFile) return null;

    const formData = new FormData();
    formData.append('avatar', avatarFile);
    formData.append('user_id', profileData.id);

    try {
      const token = await getToken();
      const response = await fetch(API_ENDPOINTS.PROFILE_AVATAR, {
        method: 'POST',
        headers: {
          'Authorization': token ? `Bearer ${token}` : undefined
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to upload avatar');
      }

      const result = await response.json();
      return result.avatar_url;
    } catch (error) {
      console.error('Error uploading avatar:', error);
      throw error;
    }
  };

  // Validation function for ORCID
  const validateORCID = async (orcid) => {
    if (!orcid) return { valid: true }; // ORCID is optional
    
    // Format validation: ORCID should be in format XXXX-XXXX-XXXX-XXXX
    const orcidRegex = /^\d{4}-\d{4}-\d{4}-\d{3}[0-9X]$/;
    if (!orcidRegex.test(orcid)) {
      return {
        valid: false,
        message: 'Invalid ORCID format. Please use format: XXXX-XXXX-XXXX-XXXX'
      };
    }

    try {
      // Check if ORCID exists via ORCID API
      const response = await fetch(`https://pub.orcid.org/v3.0/${orcid}`, {
        headers: {
          'Accept': 'application/json'
        }
      });

      if (response.status === 404) {
        return {
          valid: false,
          message: 'ORCID not found. Please verify your ORCID identifier.'
        };
      }

      if (!response.ok) {
        // If API is down or rate limited, allow the submission but log warning
        console.warn('ORCID API unavailable, skipping validation');
        return { valid: true, warning: true };
      }

      return { valid: true };
    } catch (error) {
      console.error('Error validating ORCID:', error);
      // If network error, allow submission but log warning
      return { valid: true, warning: true };
    }
  };  // Validation function for OpenReview ID
  const validateOpenReviewId = (openreviewId) => {
    if (!openreviewId) return { valid: true }; // OpenReview ID is optional
    
    // Check if OpenReview ID starts with '~'
    if (!openreviewId.startsWith('~')) {
      return {
        valid: false,
        message: 'Invalid OpenReview ID format. OpenReview ID should start with "~" (e.g., ~John_Doe1)'
      };
    }
    
    return { valid: true };
  };

  // Validation function for Google Scholar URL
  const validateGoogleScholarUrl = (url) => {
    if (!url) return { valid: true }; // Google Scholar URL is optional
    
    // Check if URL starts with the correct Google Scholar domain
    const validPrefixes = [
      'https://scholar.google.com/citations?user=',
      'https://scholar.google.co.uk/citations?user=',
      'https://scholar.google.ca/citations?user=',
      'http://scholar.google.com/citations?user=',
      'http://scholar.google.co.uk/citations?user=',
      'http://scholar.google.ca/citations?user='
    ];
    
    const isValid = validPrefixes.some(prefix => url.startsWith(prefix));
    
    if (!isValid) {
      return {
        valid: false,
        message: 'Invalid Google Scholar URL. Please provide a URL in the format: https://scholar.google.com/citations?user=YOUR_ID'
      };
    }

    return { valid: true };
  };
  const handleSave = async () => {
    // Validate required fields
    if (!formData.institutionalEmail) {
      alert('Institutional Email is required');
      return;
    }
    
    // Validate at least one academic identifier
    if (!formData.orcid && !formData.googleScholarUrl && !formData.openreviewId) {
      alert('Please provide at least one academic identifier (ORCID, Google Scholar URL, or OpenReview ID)');
      return;
    }
    
    // Validate academic identifiers
    setIsLoading(true);
    
    try {
      // Validate ORCID if provided
      if (formData.orcid) {
        const orcidValidation = await validateORCID(formData.orcid);
        if (!orcidValidation.valid) {
          alert(orcidValidation.message);
          setIsLoading(false);
          return;
        }
        if (orcidValidation.warning) {
          console.warn('ORCID validation skipped due to API unavailability');
        }
      }
        // Validate OpenReview ID if provided
      if (formData.openreviewId) {
        const openreviewValidation = validateOpenReviewId(formData.openreviewId);
        if (!openreviewValidation.valid) {
          alert(openreviewValidation.message);
          setIsLoading(false);
          return;
        }
      }
      
      // Validate Google Scholar URL if provided
      if (formData.googleScholarUrl) {
        const scholarValidation = validateGoogleScholarUrl(formData.googleScholarUrl);
        if (!scholarValidation.valid) {
          alert(scholarValidation.message);
          setIsLoading(false);
          return;
        }
      }
    } catch (error) {
      console.error('Validation error:', error);
      alert('An error occurred during validation. Please try again.');
      setIsLoading(false);
      return;
    }
    
    try {
      let avatarUrl = null;
      
      // Upload avatar first if there's a new one
      if (avatarFile) {
        try {
          avatarUrl = await handleAvatarUpload();
        } catch (error) {
          console.error('Avatar upload failed:', error);
          // Continue with profile update even if avatar upload fails
        }
      }

      // Prepare profile data - filter out empty URL strings
      const profilePayload = {
        user_id: profileData.id,
        name: formData.name,
        title: formData.title || null,
        affiliation: formData.affiliation || null,
        location: formData.location || null,
        bio: formData.bio || null,
        email: formData.email || null,
        website: formData.website || null,
        institutional_email: formData.institutionalEmail || null,
        orcid: formData.orcid || null,
        google_scholar_url: formData.googleScholarUrl || null,
        openreview_id: formData.openreviewId || null,
        academic_status: formData.academicStatus || null,
        default_category: formData.defaultCategory || null,
        github: formData.github || null,
        twitter: formData.twitter || null,
        linkedin: formData.linkedin || null
      };
      
      // Add avatar URL if successfully uploaded
      if (avatarUrl) {
        profilePayload.avatar_url = avatarUrl;
      }

      // Call the backend API to save profile
      const token = await getToken();
      const response = await fetch(API_ENDPOINTS.PROFILE_UPDATE, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : undefined
        },
        body: JSON.stringify(profilePayload)
      });

      if (!response.ok) {
        throw new Error('Failed to update profile');
      }      const result = await response.json();
      console.log('Profile updated successfully:', result);
        // Update local profile data with the saved data
      setProfileData({
        ...profileData,
        name: result.name || profileData.name,
        title: result.title || '',
        affiliation: result.affiliation || '',
        location: result.location || '',
        avatar: result.avatar_url || profileData.avatar,
        bio: result.bio || '',
        email: result.email || '',
        website: result.website || '',
        institutionalEmail: result.institutional_email || '',
        orcid: result.orcid || '',
        googleScholarUrl: result.google_scholar_url || '',
        openreviewId: result.openreview_id || '',
        academicStatus: result.academic_status || '',
        defaultCategory: result.default_category || '',
        isVerified: result.is_verified === 1 || result.is_verified === true, // Update verification status
        socialLinks: {
          github: result.github_url || '',
          twitter: result.twitter_url || '',
          linkedin: result.linkedin_url || ''
        }
      });
      
      // Exit edit mode after successful save
      setIsEditMode(false);
      setFormData(null);
      setAvatarPreview(null);
      setAvatarFile(null);
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Failed to update profile. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setIsEditMode(false);
    setFormData(null);
    setAvatarPreview(null);
    setAvatarFile(null);
  };

  // const papers = [
  //   {
  //     id: 1,
  //     title: 'Attention Is All You Need for Multimodal Learning',
  //     authors: 'Sarah Chen, David Kim, Michael Zhang',
  //     venue: 'ICML 2024',
  //     date: '2024-01-15',
  //     citations: 342,
  //     downloads: 1205,
  //     status: 'published',
  //     tags: ['machine learning', 'attention mechanisms', 'multimodal']
  //   },
  //   {
  //     id: 2,
  //     title: 'Ethical Considerations in Large Language Model Deployment',
  //     authors: 'Sarah Chen, Alice Johnson',
  //     venue: 'Under Review - NeurIPS 2024',
  //     date: '2024-02-20',
  //     citations: 0,
  //     downloads: 89,
  //     status: 'under-review',
  //     tags: ['ethics', 'large language models', 'AI safety']
  //   },
  //   {
  //     id: 3,
  //     title: 'Federated Learning with Differential Privacy Guarantees',
  //     authors: 'Sarah Chen, Robert Liu, Emma Davis',
  //     venue: 'ICLR 2023',
  //     date: '2023-05-12',
  //     citations: 156,
  //     downloads: 892,
  //     status: 'published',
  //     tags: ['federated learning', 'privacy', 'distributed systems']
  //   }
  // ];

  // const reviews = [
  //   {
  //     id: 1,
  //     title: 'Neural Architecture Search for Efficient Models',
  //     venue: 'ICML 2024',
  //     date: '2024-01-10',
  //     score: 7,
  //     decision: 'accept'
  //   },
  //   {
  //     id: 2,
  //     title: 'Quantum Machine Learning Applications',
  //     venue: 'NeurIPS 2024',
  //     date: '2024-02-05',
  //     score: 5,
  //     decision: 'reject'
  //   }
  // ];

  const TabButton = ({ id, label, isActive, onClick }) => (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
        isActive
          ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
          : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
      }`}
    >
      {label}
    </button>
  );

  const StatCard = ({ label, value, icon: Icon, trend }) => (
    <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 border border-gray-100 dark:border-gray-700 group hover:scale-105">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">{label}</p>
          <p className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">{value}</p>
          {trend && (
            <p className="text-sm text-green-600 dark:text-green-400 flex items-center gap-1 mt-2">
              <TrendingUp className="w-3 h-3" />
              {trend}
            </p>
          )}
        </div>
        <div className="p-3 bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-lg group-hover:scale-110 transition-transform">
          <Icon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
        </div>
      </div>
    </div>
  );

  const PaperCard = ({ paper }) => (
    <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-shadow">
      <div className="flex justify-between items-start mb-3">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white line-clamp-2">
          {paper.title}
        </h3>
        <span className={`px-2 py-1 text-xs rounded-full ${
          paper.status === 'published' 
            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
        }`}>
          {paper.status === 'published' ? 'Published' : 'Under Review'}
        </span>
      </div>
      
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{paper.authors}</p>
      <p className="text-sm text-blue-600 dark:text-blue-400 mb-3">{paper.venue}</p>
      
      <div className="flex flex-wrap gap-2 mb-4">
        {paper.tags?.map((tag, index) => ( 
          <span key={index} className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs rounded">
            {tag}
          </span>
        ))}
      </div>
      
      <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
        <span>{new Date(paper.date).toLocaleDateString()}</span>
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1">
            <MessageSquare className="w-4 h-4" />
            {paper.citations}
          </span>
          <span className="flex items-center gap-1">
            <Download className="w-4 h-4" />
            {paper.downloads}
          </span>
          <div className="flex gap-2">
            <button className="text-gray-400 hover:text-red-500 transition-colors">
              <Heart className="w-4 h-4" />
            </button>
            <button className="text-gray-400 hover:text-blue-500 transition-colors">
              <Share2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Profile Header */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden mb-8">
          <div className="bg-gradient-to-r from-blue-500 to-purple-600 h-32"></div>
          <div className="px-8 pb-8 -mt-16">
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Avatar and Basic Info */}
              <div className="flex flex-col items-center lg:items-start">
                <div className="relative">
                {isEditMode ? (
                  <>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleAvatarChange}
                      className="hidden"
                    />
                    <div 
                      onClick={() => fileInputRef.current?.click()}
                      className="relative w-40 h-40 rounded-full overflow-hidden border-4 border-white dark:border-gray-700 shadow-2xl cursor-pointer group"
                    >
                      {avatarPreview ? (
                        <img
                          src={avatarPreview}
                          alt="Avatar preview"
                          className="w-full h-full object-cover"
                        />
                      ) : profileData?.avatar ? (
                        <img
                          src={profileData.avatar}
                          alt={profileData.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                          <User className="w-16 h-16 text-gray-400" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 flex items-center justify-center rounded-full transition-all">
                        <div className="text-white text-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <Camera className="w-8 h-8 mx-auto mb-1" />
                          <span className="text-xs">Change Photo</span>
                        </div>
                      </div>
                    </div>
                    {avatarFile && (
                      <button 
                        onClick={() => {
                          setAvatarFile(null);
                          setAvatarPreview(null);
                        }}
                        className="absolute top-0 right-0 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                      >
                        <span className="text-xs">✕</span>
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    {profileData?.avatar ? (
                      <img
                        src={profileData.avatar}
                        alt={profileData.name}
                        className="w-40 h-40 rounded-full object-cover border-4 border-white dark:border-gray-700 shadow-2xl"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : null}
                    {!profileData?.avatar && (
                      <div className="w-40 h-40 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 border-4 border-white dark:border-gray-700 shadow-2xl flex items-center justify-center">
                        <User className="w-16 h-16 text-white" />
                      </div>
                    )}
                    {isOwnProfile && (
                      <button 
                        onClick={handleEditClick}
                        className="absolute bottom-2 right-2 p-2.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-110"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                    )}
                  </>
                )}
              </div>
              
              <div className="mt-6 text-center lg:text-left">
                {isEditMode ? (
                  <div className="space-y-3">
                    <div>
                      <input
                        type="text"
                        name="name"
                        value={formData?.name || ''}
                        onChange={handleInputChange}
                        className="text-3xl font-bold text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 rounded-lg px-4 py-2 w-full focus:ring-2 focus:ring-blue-500 focus:outline-none transition-all"
                        placeholder="Your Name"
                      />
                    </div>
                    <div>
                      <input
                        type="text"
                        name="title"
                        value={formData?.title || ''}
                        onChange={handleInputChange}
                        className="text-lg text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 rounded-lg px-4 py-2 w-full focus:ring-2 focus:ring-blue-500 focus:outline-none transition-all"
                        placeholder="Professional Title"
                      />
                    </div>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <input
                          type="text"
                          name="affiliation"
                          value={formData?.affiliation || ''}
                          onChange={handleInputChange}
                          className="text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 rounded-lg px-4 py-2 w-full focus:ring-2 focus:ring-blue-500 focus:outline-none transition-all"
                          placeholder="Organization"
                        />
                      </div>
                      <div className="flex-1">
                        <input
                          type="text"
                          name="location"
                          value={formData?.location || ''}
                          onChange={handleInputChange}
                          className="text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 rounded-lg px-4 py-2 w-full focus:ring-2 focus:ring-blue-500 focus:outline-none transition-all"
                          placeholder="Location"
                        />
                      </div>
                    </div>
                  </div>                ) : (
                  <>
                    <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">{profileData.name}</h1>
                    
                    {/* Verification Status Badge */}
                    <div className="mb-3">
                      {profileData.isVerified ? (
                        <span className="inline-flex items-center gap-2 px-3 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded-full text-sm font-semibold">
                          <Award className="w-4 h-4" />
                          Verified
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-2 px-3 py-1 bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 rounded-full text-sm font-semibold">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          Unverified
                        </span>
                      )}
                    </div>
                    
                    {profileData.title && (
                      <p className="text-xl text-gray-600 dark:text-gray-400 mb-3">{profileData.title}</p>
                    )}
                    <div className="flex flex-wrap gap-4 text-sm text-gray-500 dark:text-gray-400">
                      {profileData.affiliation && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {profileData.affiliation}
                        </span>
                      )}
                      {profileData.location && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {profileData.location}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        Joined {profileData.joinDate}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Bio and Links */}
            <div className="flex-1 mt-8 lg:mt-0">
              {isEditMode ? (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">About Me</label>
                    <textarea
                      name="bio"
                      value={formData?.bio || ''}
                      onChange={handleInputChange}
                      rows={4}
                      className="w-full px-4 py-3 text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none transition-all resize-none"
                      placeholder="Share your research interests, achievements, and what drives your work..."
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Contact Information</label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="email"
                          name="email"
                          value={formData?.email || ''}
                          onChange={handleInputChange}
                          className="w-full pl-10 pr-3 py-2 text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none transition-all"
                          placeholder="your.email@example.com"
                        />
                      </div>
                      <div className="relative">
                        <LinkIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="url"
                          name="website"
                          value={formData?.website || ''}
                          onChange={handleInputChange}
                          className="w-full pl-10 pr-3 py-2 text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none transition-all"
                          placeholder="https://yourwebsite.com"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Social Media</label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="relative">
                        <Github className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="url"
                          name="github"
                          value={formData?.github || ''}
                          onChange={handleInputChange}
                          className="w-full pl-10 pr-3 py-2 text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none transition-all"
                          placeholder="GitHub profile"
                        />
                      </div>
                      <div className="relative">
                        <Twitter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="url"
                          name="twitter"
                          value={formData?.twitter || ''}
                          onChange={handleInputChange}
                          className="w-full pl-10 pr-3 py-2 text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none transition-all"
                          placeholder="Twitter profile"
                        />
                      </div>                      <div className="relative">
                        <Linkedin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="url"
                          name="linkedin"
                          value={formData?.linkedin || ''}
                          onChange={handleInputChange}
                          className="w-full pl-10 pr-3 py-2 text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none transition-all"
                          placeholder="LinkedIn profile"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Academic Information Section */}
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                    <h3 className="text-lg font-bold text-gray-800 dark:text-gray-200 mb-4 flex items-center">
                      <BookOpen className="w-5 h-5 mr-2 text-blue-600" />
                      Academic Information
                    </h3>
                    
                    {/* Required Fields */}
                    <div className="mb-4">
                      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        Institutional Email <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="email"
                          name="institutionalEmail"
                          value={formData?.institutionalEmail || ''}
                          onChange={handleInputChange}
                          required
                          className="w-full pl-10 pr-3 py-2 text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none transition-all border border-gray-300 dark:border-gray-600"
                          placeholder="your.name@institution.edu"
                        />
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Use your official institutional email address</p>
                    </div>

                    {/* Academic Status */}
                    <div className="mb-4">
                      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        Academic Status
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <select
                          name="academicStatus"
                          value={formData?.academicStatus || ''}
                          onChange={handleInputChange}
                          className="w-full pl-10 pr-3 py-2 text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none transition-all border border-gray-300 dark:border-gray-600 appearance-none cursor-pointer"
                        >
                          <option value="">Select your status...</option>
                          <option value="grad_student">Graduate Student</option>
                          <option value="postdoc">Postdoctoral Researcher</option>
                          <option value="professor">Professor</option>
                          <option value="staff">Research Staff</option>
                          <option value="other">Other</option>
                        </select>
                        <ChevronRight className="absolute right-3 top-1/2 transform -translate-y-1/2 rotate-90 w-4 h-4 text-gray-400 pointer-events-none" />
                      </div>
                    </div>

                    {/* Research Category */}
                    <div className="mb-4">
                      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        Primary Research Area
                      </label>
                      <div className="relative">
                        <BookOpen className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          name="defaultCategory"
                          value={formData?.defaultCategory || ''}
                          onChange={handleInputChange}
                          className="w-full pl-10 pr-3 py-2 text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none transition-all border border-gray-300 dark:border-gray-600"
                          placeholder="e.g., Machine Learning, Quantum Physics, Bioinformatics"
                        />
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Your main field of research</p>
                    </div>

                    {/* Academic Identifiers - At least one required */}                    <div>
                      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        Academic Identifiers <span className="text-red-500">* (at least one required)</span>
                      </label>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">ORCID</label>
                          <input
                            type="text"
                            name="orcid"
                            value={formData?.orcid || ''}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none transition-all border border-gray-300 dark:border-gray-600"
                            placeholder="e.g., 0000-0002-1825-0097"
                          />
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Format: XXXX-XXXX-XXXX-XXXX</p>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Google Scholar URL</label>
                          <input
                            type="url"
                            name="googleScholarUrl"
                            value={formData?.googleScholarUrl || ''}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none transition-all border border-gray-300 dark:border-gray-600"
                            placeholder="https://scholar.google.com/citations?user=..."
                          />
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Your full profile URL</p>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">OpenReview ID</label>
                          <input
                            type="text"
                            name="openreviewId"
                            value={formData?.openreviewId || ''}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none transition-all border border-gray-300 dark:border-gray-600"
                            placeholder="e.g., ~John_Doe1"
                          />
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Usually starts with ~</p>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        <strong>Note:</strong> Identifiers will be validated before saving. Please ensure they are correct.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {profileData.bio && (
                    <div className="mb-6">
                      <h3 className="text-sm font-bold text-gray-600 dark:text-gray-300 uppercase tracking-wider mb-3">About</h3>
                      <p className="text-base lg:text-lg text-gray-900 dark:text-gray-100 leading-relaxed">{profileData.bio}</p>
                    </div>
                  )}
                  {/* Contact Links */}
                  {(profileData.email || profileData.website || profileData.socialLinks.github || profileData.socialLinks.twitter || profileData.socialLinks.linkedin) && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider mb-3">Connect</h3>
                      <div className="flex flex-wrap gap-3">
                        {profileData.email && (
                          <a href={`mailto:${profileData.email}`} className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900 hover:text-blue-600 dark:hover:text-blue-400 transition-all">
                            <Mail className="w-4 h-4" />
                            <span className="text-sm">Email</span>
                          </a>
                        )}
                        {profileData.website && (
                          <a href={profileData.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900 hover:text-blue-600 dark:hover:text-blue-400 transition-all">
                            <LinkIcon className="w-4 h-4" />
                            <span className="text-sm">Website</span>
                          </a>
                        )}
                        {profileData.socialLinks.github && (
                          <a href={profileData.socialLinks.github} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-900 dark:hover:bg-gray-600 hover:text-white transition-all">
                            <Github className="w-4 h-4" />
                            <span className="text-sm">GitHub</span>
                          </a>
                        )}
                        {profileData.socialLinks.twitter && (
                          <a href={profileData.socialLinks.twitter} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-blue-400 hover:text-white transition-all">
                            <Twitter className="w-4 h-4" />
                            <span className="text-sm">Twitter</span>
                          </a>
                        )}
                        {profileData.socialLinks.linkedin && (
                          <a href={profileData.socialLinks.linkedin} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-blue-600 hover:text-white transition-all">
                            <Linkedin className="w-4 h-4" />
                            <span className="text-sm">LinkedIn</span>
                          </a>                        )}                      </div>
                    </div>
                  )}
                    {/* Verification Warning for Unverified Users */}
                  {!profileData.isVerified && isOwnProfile && (
                    <div className="mt-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 dark:border-yellow-600 rounded-r-lg">
                      <div className="flex items-start gap-3">
                        <svg className="w-6 h-6 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <div className="flex-1">
                          <h4 className="text-sm font-bold text-yellow-800 dark:text-yellow-200 mb-1">
                            Account Not Verified
                          </h4>
                          <p className="text-sm text-yellow-700 dark:text-yellow-300 mb-2">
                            Your academic identity has not been verified yet. Please provide at least one of the following identifiers in your profile:
                          </p>
                          <ul className="text-sm text-yellow-700 dark:text-yellow-300 space-y-1 mb-3 ml-4 list-disc">
                            <li><strong>ORCID</strong> - Your unique researcher identifier</li>
                            <li><strong>Google Scholar URL</strong> - Link to your Google Scholar profile</li>
                            <li><strong>OpenReview ID</strong> - Your OpenReview profile ID</li>
                          </ul>
                          <div className="bg-yellow-100 dark:bg-yellow-900/40 p-3 rounded-lg border border-yellow-300 dark:border-yellow-700 mb-4">
                            <p className="text-sm font-semibold text-yellow-900 dark:text-yellow-100 flex items-center gap-2">
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                              </svg>
                              Important: You cannot submit papers until your account is verified.
                            </p>                          </div>
                          {/* Edit Profile Button */}
                          <button 
                            onClick={handleEditClick}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-yellow-600 hover:bg-yellow-700 dark:bg-yellow-500 dark:hover:bg-yellow-600 text-white rounded-md transition-all shadow-sm hover:shadow-md font-medium"
                          >
                            <Edit className="w-3.5 h-3.5" />
                            Edit Profile
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Academic Information Display */}
                  {(profileData.institutionalEmail || profileData.academicStatus || profileData.defaultCategory || profileData.orcid || profileData.googleScholarUrl || profileData.openreviewId) && (
                    <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-6">
                      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider mb-4 flex items-center">
                        <BookOpen className="w-4 h-4 mr-2" />
                        Academic Profile
                      </h3>
                      
                      <div className="space-y-4">
                        {/* Academic Status and Category */}
                        <div className="flex flex-wrap gap-4">
                          {profileData.academicStatus && (
                            <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-lg border border-blue-200 dark:border-blue-800">
                              <User className="w-4 h-4" />
                              <span className="text-sm font-medium">
                                {profileData.academicStatus === 'grad_student' && 'Graduate Student'}
                                {profileData.academicStatus === 'postdoc' && 'Postdoctoral Researcher'}
                                {profileData.academicStatus === 'professor' && 'Professor'}
                                {profileData.academicStatus === 'staff' && 'Research Staff'}
                                {profileData.academicStatus === 'other' && 'Other'}
                              </span>
                            </div>
                          )}
                          {profileData.defaultCategory && (
                            <div className="flex items-center gap-2 px-4 py-2 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 rounded-lg border border-purple-200 dark:border-purple-800">
                              <BookOpen className="w-4 h-4" />
                              <span className="text-sm font-medium">{profileData.defaultCategory}</span>
                            </div>
                          )}
                        </div>
                        
                        {/* Academic Identifiers */}
                        {(profileData.orcid || profileData.googleScholarUrl || profileData.openreviewId) && (
                          <div>
                            <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-2">Academic Identifiers</p>
                            <div className="flex flex-wrap gap-3">
                              {profileData.orcid && (
                                <a 
                                  href={`https://orcid.org/${profileData.orcid}`} 
                                  target="_blank" 
                                  rel="noopener noreferrer" 
                                  className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/30 transition-all border border-green-200 dark:border-green-800"
                                >
                                  <svg className="w-4 h-4" viewBox="0 0 256 256" fill="currentColor">
                                    <path d="M256 128c0 70.7-57.3 128-128 128S0 198.7 0 128 57.3 0 128 0s128 57.3 128 128z" fill="currentColor"/>
                                    <path d="M86.3 186.2H70.9V79.1h15.4v107.1zM108.9 79.1h41.6c39.6 0 57 28.3 57 53.6 0 27.5-21.5 53.6-56.8 53.6h-41.8V79.1zm15.4 93.3h24.5c34.9 0 42.9-26.5 42.9-39.7C191.7 111.2 178 93 148 93h-23.7v79.4zM71.3 54.8c5.2 0 9.4 4.2 9.4 9.4s-4.2 9.4-9.4 9.4-9.4-4.2-9.4-9.4 4.2-9.4 9.4-9.4z" fill="white"/>
                                  </svg>
                                  <span className="text-sm font-medium">ORCID</span>
                                </a>
                              )}
                              {profileData.googleScholarUrl && (
                                <a 
                                  href={profileData.googleScholarUrl} 
                                  target="_blank" 
                                  rel="noopener noreferrer" 
                                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-all border border-blue-200 dark:border-blue-800"
                                >
                                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 24a7 7 0 110-14 7 7 0 010 14zm0-24L0 9.5l4.838 3.94A8 8 0 0112 9a8 8 0 017.162 4.44L24 9.5z"/>
                                  </svg>
                                  <span className="text-sm font-medium">Google Scholar</span>
                                </a>
                              )}
                              {profileData.openreviewId && (
                                <a 
                                  href={`https://openreview.net/profile?id=${profileData.openreviewId}`} 
                                  target="_blank" 
                                  rel="noopener noreferrer" 
                                  className="inline-flex items-center gap-2 px-4 py-2 bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 rounded-lg hover:bg-orange-100 dark:hover:bg-orange-900/30 transition-all border border-orange-200 dark:border-orange-800"
                                >
                                  <BookOpen className="w-4 h-4" />
                                  <span className="text-sm font-medium">OpenReview</span>
                                </a>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Badges
              {!isEditMode && profileData.badges && profileData.badges.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Achievements</h3>
                  <div className="flex flex-wrap gap-2">
                    {profileData.badges.map((badge, index) => (
                      <span key={index} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${badge.color} shadow-sm`}>
                        <badge.icon className="w-4 h-4" />
                        {badge.name}
                      </span>
                    ))}
                  </div>
                </div>
              )} */}

              {/* Action Buttons */}
              <div className="flex gap-3 mt-8">
                {isOwnProfile ? (
                  isEditMode ? (
                    <>
                      <button 
                        onClick={handleSave}
                        disabled={isLoading}
                        className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg hover:from-green-600 hover:to-emerald-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isLoading ? (
                          <>
                            <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                            Saving...
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Save Changes
                          </>
                        )}
                      </button>
                      <button 
                        onClick={handleCancel}
                        disabled={isLoading}
                        className="flex items-center gap-2 px-6 py-2.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        Cancel
                      </button>
                    </>
                  ) : null
                ) : (
                  <>
                    <button className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl">
                      <User className="w-4 h-4" />
                      Follow
                    </button>
                    <button className="flex items-center gap-2 px-6 py-2.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-all shadow-md hover:shadow-lg">
                      <Mail className="w-4 h-4" />
                      Message
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
          </div>
        </div>

        {/* Stats Grid
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-8">
          <StatCard label="Papers" value={profileData.stats.papers} icon={BookOpen} trend="+3 this month" />
          <StatCard label="Citations" value={profileData.stats.citations.toLocaleString()} icon={MessageSquare} trend="+127 this month" />
          <StatCard label="H-Index" value={profileData.stats.hIndex} icon={BarChart3} />
          <StatCard label="Followers" value={profileData.stats.followers.toLocaleString()} icon={Users} trend="+45 this month" />
          <StatCard label="Following" value={profileData.stats.following} icon={User} />
          <StatCard label="Downloads" value={profileData.stats.downloads.toLocaleString()} icon={Download} trend="+892 this month" />
        </div> */}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-200 dark:border-gray-700">
          <TabButton
            id="papers"
            label="Papers"
            isActive={activeTab === 'papers'}
            onClick={() => setActiveTab('papers')}
          />
          {/* <TabButton
            id="reviews"
            label="Reviews"
            isActive={activeTab === 'reviews'}
            onClick={() => setActiveTab('reviews')}
          />
          <TabButton
            id="activity"
            label="Activity"
            isActive={activeTab === 'activity'}
            onClick={() => setActiveTab('activity')}
          />
          <TabButton
            id="collaborations"
            label="Collaborations"
            isActive={activeTab === 'collaborations'}
            onClick={() => setActiveTab('collaborations')}
          /> */}
        </div>
        {/* Tab Content */}
        <div className="space-y-6">
          {activeTab === 'papers' && (
            <div className="space-y-4">
                {papersLoading ? (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">Loading papers...</div>
                ) : papersError ? (
                    <div className="text-center py-8 text-red-500 dark:text-red-400">Error: {papersError}</div>
                ) : userPapers.length > 0 ? (
                  userPapers.map((paper) => (
                    <PaperCard key={paper.id || paper.aixiv_id} paper={paper} />
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">No papers found for this user.</div>
                )}
            </div>
          )}
          {/* {activeTab === 'reviews' && (
            <div className="space-y-4">
              {reviews.map((review) => (
                <div key={review.id} className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{review.title}</h3>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      review.decision === 'accept' 
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                        : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                    }`}>
                      {review.decision}
                    </span>
                  </div>
                  <p className="text-sm text-blue-600 dark:text-blue-400 mb-2">{review.venue}</p>
                  <div className="flex justify-between items-center text-sm text-gray-500 dark:text-gray-400">
                    <span>{new Date(review.date).toLocaleDateString()}</span>
                    <span className="flex items-center gap-1">
                      <Star className="w-4 h-4" />
                      Score: {review.score}/10
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'activity' && (
            <div className="bg-white dark:bg-gray-800 p-8 rounded-xl border border-gray-200 dark:border-gray-700 text-center">
              <BarChart3 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Activity Timeline</h3>
              <p className="text-gray-600 dark:text-gray-400">Activity visualization coming soon</p>
            </div>
          )}

          {activeTab === 'collaborations' && (
            <div className="bg-white dark:bg-gray-800 p-8 rounded-xl border border-gray-200 dark:border-gray-700 text-center">
              <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Collaboration Network</h3>
              <p className="text-gray-600 dark:text-gray-400">Collaboration visualization coming soon</p>
            </div>
          )} */}
        </div>
      </div>
    </div>
  );
};

export default Profile;
